import {Injectable, ViewContainerRef} from '@angular/core';
import * as selfScheduling from "../interfaces/Iself.scheduling";
import {
	IDaysTimeOptions,
	ILocationTreeNode,
	IReservation,
	ISelfLocation,
	OperatingSystem, ScheduleOpCode
} from "../interfaces/Iself.scheduling";
import * as moment from 'moment';
import {SelfSchedulingService} from './self.scheduling.service';
import {map} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import * as _ from "lodash";
import {SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.manager";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {ISearchResult} from "../interfaces/Iself.scheduling";
import { SelfSchedulingDocumentAcquisitionService } from './self.scheduling.document.acquisition.services';

/**
 ****************************************************************
 * Service class for holding self scheduling flow data.
 *
 * Created by Shai Zelman on 18/03/2020.
 ****************************************************************
 */
@Injectable({providedIn: "root"})
export class SelfSchedulingDataService {
	// Global
	public notificationContainerRef: ViewContainerRef;
	public animateMainScreenLoading = false;

	public patientOrders: selfScheduling.IPatientAppointment[];
	public patientScheduledExams: selfScheduling.IPatientAppointment[];
	public patientPerformedExams: selfScheduling.IPatientAppointment[];

	public timeToClosestAppointment: selfScheduling.ITimeToClosestAppointment;
	public timeToClosestAppointmentString: string;
	public timeToClosestAppointmentParameters: number[];

	public selectedPatientAppointment: selfScheduling.IPatientAppointment;
	public selectedPatientAppointments: selfScheduling.IPatientAppointment[];

	public selectedLocations: ISelfLocation[];
	public locationsTree: ILocationTreeNode[];

	public reservation: IReservation;

	// New order and schedule
	public isNewOrderAndScheduleAllowed: boolean = false;
	public selectedModalityTypeGroup: selfScheduling.ISelfModalityTypeGroup;
	public selectedSpsCodes: selfScheduling.ISpsCode[];
	public additionalInfoPatientModel: any;

	// Scheduling
	public selectedScheduling: selfScheduling.ISearchResult[];

	// Scheduled Data that is displayed in summary page and when clicking
	public scheduledAppointments: selfScheduling.IScheduledAppointment[];
	public dateTimeOptions: IDaysTimeOptions;

	// Data
	public selectedStudies: selfScheduling.ISelectedStudy[];

	// Routing and navigation
	public skippedSubpages: {[url: string]: boolean};
	public isNavigatingBack: boolean;

	// Form data
	public formData: any;

	// Scheduling not available
	public schedulingNotAvailableMessage: string;

	public navigationConfirmationPopupSubject: Subject<void> = new Subject<void>();

	constructor(private selfSchedulingService: SelfSchedulingService, private cfg: SystemConfigurationValues, private sanitizer: DomSanitizer, private documentAcquisitionService: SelfSchedulingDocumentAcquisitionService) {
		this.patientOrders = [];
		this.patientScheduledExams = [];
		this.patientPerformedExams = [];
		this.selectedSpsCodes = [];
		this.dateTimeOptions = { startTime: null, endTime: null, weekdays: [] };
	}

	get selectedSchedulingLocations(): number[] {
		return this.selectedLocations && this.selectedLocations.length > 0 ?
			this.selectedLocations.map(location => location.orgStructureKey) :
			_.map(this.locationsTree, 'location.orgStructureKey');
	}

	get operatingSystem(): OperatingSystem {
		return this.getOperatingSystem();
	}

	public setSelectedStudies(patientAppointments: selfScheduling.IPatientAppointment[]) {
		if (patientAppointments.length === 1 && patientAppointments[0].spsID <= 0) {
			this.selectedStudies = _.flatMap(patientAppointments[0].orderedProcedures, (orderedProcedure: selfScheduling.IPatientAppointment) => {
				return {
					spsID: -1,
					spsCodeKey: orderedProcedure.spsCodeKey,
					requestedProcedureID: patientAppointments[0].requestedProcedureID,
					rpCodeKey: 0,
					procedureKeys: null,
					duration: orderedProcedure.duration,
					durationModifier: null,
					start: orderedProcedure.startDateTime,
					end: orderedProcedure.endDateTime,
					arriveMinBefore: patientAppointments[0].arriveMinBefore,
					modalityKey: orderedProcedure.modalityKey,
					personReferenceTypeKeys: null,
					selectedResourceAllocation: null,
					studySeriesKey: null,
					seriesSequenceId: null,
					isCurrentContextStudy: null
				};
			});
		} else {
			this.selectedStudies = _.flatMap(patientAppointments, (patientAppointment: selfScheduling.IPatientAppointment) => {
				return {
					spsID: patientAppointment.spsID,
					spsCodeKey: patientAppointment.spsCodeKey,
					requestedProcedureID: patientAppointment.requestedProcedureID,
					rpCodeKey: 0,
					procedureKeys: null,
					duration: patientAppointment.duration,
					durationModifier: null,
					start: patientAppointment.startDateTime,
					end: patientAppointment.endDateTime,
					arriveMinBefore: patientAppointment.arriveMinBefore,
					modalityKey: patientAppointment.modalityKey,
					personReferenceTypeKeys: null,
					selectedResourceAllocation: null,
					studySeriesKey: null,
					seriesSequenceId: null,
					isCurrentContextStudy: null
				};
			});
		}
	}

	public getOpCodeKey(opCode: ScheduleOpCode): number {
		switch (opCode) {
			case selfScheduling.ScheduleOpCode.Reschedule:
				return this.cfg.CanRescheduleOpCodeKey;

			case selfScheduling.ScheduleOpCode.NewOrderSchedule:
				return this.cfg.CanNewOrderAndScheduleOpCodeKey;

			case selfScheduling.ScheduleOpCode.EditForm:
				return this.cfg.CanEditFormOpCodeKey;

			case selfScheduling.ScheduleOpCode.OrderSchedule:
			default:
				return this.cfg.CanScheduleOpCodeKey;
		}
	}

	public refreshData(): Observable<selfScheduling.IPatientAppointment[]> {
		return this.selfSchedulingService.getOrdersAndAppointments().pipe(map
		((results: selfScheduling.IPatientAppointment[]) => {
			this.setData(results);
			return results;
		}));
	}

	public setData(data: selfScheduling.IPatientAppointment[]) {
		this.patientOrders = data.filter((result) => result.orderKey > 0 && result.spsID <= 0);
		this.patientScheduledExams = data.filter((result) => result.spsID > 0 && result.ppsKey <= 0)
			.sort((x, y) => moment(x.startDateTime).diff(y.startDateTime));
		this.patientPerformedExams = data.filter((result) => result.ppsKey > 0);

		const earliestAppointmentTime: moment.Moment = this.patientScheduledExams.length === 0 ? null : moment(this.patientScheduledExams[0].startDateTime);

		this.timeToClosestAppointment = this.daysAndWeeksToDate(earliestAppointmentTime, "ClosestAppointment");
	}

	public clearData(clearAppointments: boolean = false) {
		if (clearAppointments) {
			this.patientOrders = [];
			this.patientScheduledExams = [];
			this.patientPerformedExams = [];

			this.timeToClosestAppointment = null;
		}

		this.selectedPatientAppointment = null;
		this.selectedPatientAppointments = [];
		this.selectedLocations = [];
		this.additionalInfoPatientModel = null;
		this.dateTimeOptions.weekdays = null;
		this.dateTimeOptions.startTime = null;
		this.dateTimeOptions.endTime = null;
		this.locationsTree = [];
		this.selectedModalityTypeGroup = null;
		this.selectedSpsCodes = [];
		this.selectedScheduling = [];
		this.scheduledAppointments = [];
		this.selectedStudies = [];
		this.skippedSubpages = {};
		this.reservation = null;
		this.formData = null;
		this.documentAcquisitionService.clearData();
	}

	public haveEarlyArrival(suggestions: ISearchResult[]): boolean {
		return suggestions && suggestions.some((suggestion) => suggestion.arriveMinBefore !== 0);
	}

	public hourAndMinutesToString(scheduledDateTime: Date, arriveMinBefore: number): selfScheduling.ITimeToArriveBefore {
		let dateMoment: moment.Moment = moment.isMoment(scheduledDateTime) ? scheduledDateTime : moment(scheduledDateTime);
		const totalHours: number = arriveMinBefore <= 59 ? 0 : Math.floor(arriveMinBefore / 60);
		const totalMinutes: number = arriveMinBefore <= 59 ? arriveMinBefore : arriveMinBefore % 60;
		if (totalHours > 1) {
			if (totalMinutes > 0) {
				return {
					parameters: [totalHours, totalMinutes],
					translationString: "ArriveHoursAndMinutesBeforeMsg"
				}
			}

			return {
				parameters: [totalHours],
				translationString: "ArriveHoursBeforeMsg"
			}
		} else if (totalHours === 1) {
			if (totalMinutes > 0) {
				return {
					parameters: [totalHours, totalMinutes],
					translationString: "ArriveOneHoursAndMinutesBeforeMsg"
				}
			}

			return {
				parameters: [totalHours],
				translationString: "ArriveOneHoursBeforeMsg"
			}
		}

		if (totalMinutes > 0 && totalMinutes < 60){
			return {
				parameters: [totalMinutes],
				translationString: "ArriveMinutesBeforeMsg"
			}
		}

		return null;
	};

	public daysAndWeeksToDate(date: Date | moment.Moment, stringPrefix: string): selfScheduling.ITimeToClosestAppointment {
		let dateMoment: moment.Moment = moment.isMoment(date) ? date : moment(date);

		const isToday: boolean = dateMoment.isSame(moment(), "day");
		const isTomorrow: boolean = dateMoment.isSame(moment().add(1, "day"), "day");
		const totalDays: number = dateMoment.startOf("day").diff(moment().startOf("day"), "days", true);

		const days: number = totalDays <= 7 ? totalDays : totalDays % 7;
		const weeks: number = Math.floor(totalDays / 7);

		let translationString: string;
		let parameters: number[];

		if (isToday) {
			translationString = stringPrefix + "Today";
			parameters = [];
		} else if (isTomorrow) {
			translationString = stringPrefix + "Tomorrow";
			parameters = [];
		} else if (totalDays <= 7) {
			translationString = stringPrefix + "Days";
			parameters = [days];
		} else if (totalDays <= 56) {
			translationString = stringPrefix + (days > 0 ? "Weeks" : "WeeksOnly");
			parameters = (days > 0 ? [weeks, days] : [weeks]);
		} else {
			translationString = stringPrefix + "Far";
			parameters = [];
		}

		return {
			totalDays: totalDays,
			isToday: isToday,
			isTomorrow: isTomorrow,
			weeks: weeks,
			days: days,
			translationString: translationString,
			parameters: parameters
		};
	}

	public getMapsUrl(lat: string, long: string, navigateToLocation = true): SafeUrl {
		const os = this.getOperatingSystem();

		let mapUrl ='';

		switch (os) {
			case OperatingSystem.Android:
				mapUrl = navigateToLocation ? 'geo:0,0?q=' + lat + ',' + long :
					'geo:0,0?q=' + lat + ',' + long; //todo: need to figure out a way to mark a location rather than navigate on native geo urls
				break;
			case OperatingSystem.iOS || OperatingSystem.macOS:
				mapUrl = navigateToLocation ? 'maps://maps.google.com/maps?daddr=' + lat + ',' + long + '&dirflg=d' :
					'maps://maps.google.com/maps?ll=' + lat + ',' + long;
				break;
			default:
				mapUrl = navigateToLocation ? 'https://www.google.com/maps/dir/?api=1&travelmode=driving&layer=traffic&destination=' + lat + ',' + long :
					'https://www.google.com/maps/search/?api=1&query=' + lat + ',' + long;
				break;
		}

		return this.sanitizer.bypassSecurityTrustUrl(mapUrl);
	}

	private getOperatingSystem() : OperatingSystem {
		const userAgent = navigator.userAgent || navigator.vendor;

		if (/android/i.test(userAgent)) {
			return OperatingSystem.Android;
		} else if (/iPad|iPhone|Macintosh|iPod/.test(userAgent) && 'ontouchend' in document) {
			return OperatingSystem.iOS;
		} else if (navigator.appVersion.indexOf("Win")!=-1) {
			return OperatingSystem.Windows
		} else if (/Mac|Macintosh/.test(userAgent) && !('ontouchend' in document)) {
			return OperatingSystem.macOS;
		}
	}

	public createScheduledAppointments(selectedScheduling: selfScheduling.ISearchResult[], scheduleResponseDetails: { [spsId: number]: selfScheduling.IScheduleResponseDetails}): selfScheduling.IScheduledAppointment[]  {
		return selectedScheduling.map(suggestion => {
			return {
				contactInfo: suggestion.SPS_ID < 0 ? Object.values(scheduleResponseDetails).find((detailEntry) => detailEntry.spsCodeKey == suggestion.SPS_Code_Key).location : scheduleResponseDetails[suggestion.SPS_ID].location,
				spsCodeKey: suggestion.SPS_Code_Key,
				spsID: suggestion.SPS_ID < 0 ? +Object.keys(scheduleResponseDetails).find((spsId) => scheduleResponseDetails[spsId].spsCodeKey == suggestion.SPS_Code_Key) : suggestion.SPS_ID,
				startTime: suggestion.startTime,
				arriveMinBefore: suggestion.arriveMinBefore, //currentAppointment.arriveMinBefore,
			};
		});
	}

	public IsGap(selectedScheduling): boolean {
		if(selectedScheduling.length > 1){
			let gapSum = 0;
			for(let i=0;i<selectedScheduling.length-1;i++) {
				let diff = (selectedScheduling[i].startTime.getTime() - selectedScheduling[i+1].startTime.getTime()) / 1000;
				diff = Math.abs(Math.round(diff / 60)) - selectedScheduling[i].duration;
				gapSum += diff;
			}
			if (gapSum > this.cfg.PP_ScheduleGapWarningMinutes) {
				return true;
			}
		}
		return false;
	}

	public prepareScheduleOrder(orderData: selfScheduling.IPatientAppointment) {
		this.selectedPatientAppointment = orderData;
		this.selectedPatientAppointments = [this.selectedPatientAppointment];

		let proceduresPerSpsCode: { [spsCodeKey: number]: { rpCodeKey: number, procedureKeys: selfScheduling.ISelfProcedureCodeLaterality[] } } = {};

		this.selectedPatientAppointment.orderedProcedures.forEach((orderedProcedure) => {
			if (proceduresPerSpsCode[orderedProcedure.spsCodeKey] && orderedProcedure.procedureCodeKey > 0) {
				proceduresPerSpsCode[orderedProcedure.spsCodeKey].procedureKeys.push({codeKey: orderedProcedure.procedureCodeKey, lateralityKey: orderedProcedure.lateralityKey});
			} else {
				proceduresPerSpsCode[orderedProcedure.spsCodeKey] = {
					rpCodeKey: orderedProcedure.rpCodeKey,
					procedureKeys: orderedProcedure.procedureCodeKey > 0 ? [{codeKey: orderedProcedure.procedureCodeKey, lateralityKey: orderedProcedure.lateralityKey}] : []
				};
			}
		});

		let spsId: number = -1;

		this.selectedStudies = Object.keys(proceduresPerSpsCode).map((spsCodeKeyString: string) => +spsCodeKeyString).map((spsCodeKey: number) => {
			return {
				spsID: spsId--,
				spsCodeKey: spsCodeKey,
				requestedProcedureID: null,
				rpCodeKey: proceduresPerSpsCode[spsCodeKey].rpCodeKey,
				procedureKeys: proceduresPerSpsCode[spsCodeKey].procedureKeys,
				duration: null,
				durationModifier: null,
				start: null,
				end: null,
				modalityKey: null,
				personReferenceTypeKeys: null,
				selectedResourceAllocation: null,
				studySeriesKey: null,
				seriesSequenceId: null,
				isCurrentContextStudy: null
			};
		});
	}
}
